import { Package, TicketBookingPackageWithoutResources } from '@thurstech/commons/dist/entity/venue';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form';

import PackageModal from './PackageModal';
import { useAppDispatch } from '../../../hooks/store';
import { deletePackageThunk, setPackages } from '../../../redux/reducer/listingSlice';
import { withErrorHandler } from '../../../utils/utils';
import { AddVenueFormData } from '../../../validation/operations/addOrEditProduct/ticketBooking/addVenueSchema';

interface PackagesProps {
  listingId: string;
  venueId?: string;
  isResourcesLinkedToVenue?: boolean;
  register: UseFormRegister<AddVenueFormData>;
  errors: FieldErrors<AddVenueFormData>;
  watch: UseFormWatch<AddVenueFormData>;
  trigger: UseFormTrigger<AddVenueFormData>;
  setValue: UseFormSetValue<AddVenueFormData>;
}

export const Packages: React.FC<PackagesProps> = ({
  listingId,
  venueId,
  isResourcesLinkedToVenue,
  watch,
  register,
  setValue,
  errors,
  trigger
}) => {
  const [displayPackageModal, setDisplayPackageModal] = useState<boolean>(false);
  const [packageModalIndex, setPackageModalIndex] = useState<number>(-1);
  const [packagesPreviousState, setPackagePreviousState] = useState<Package[]>([]);
  const dispatch = useAppDispatch();

  const packages = (watch('packages') ?? []) as Package[];

  useEffect(() => {
    if (!venueId) {
      // Only need to do this if a new venue is being added
      packages.forEach((pkg, index) => {
        setValue(
          isResourcesLinkedToVenue ? `packages.${index}.resources` : `packages.${index}.resourcesToBook`,
          undefined
        );
      });
    }
  }, [isResourcesLinkedToVenue]);

  const handlePackageModelOpen = (index: number) => {
    setPackagePreviousState(_.cloneDeep(packages));
    setPackageModalIndex(index);
    setDisplayPackageModal(true);
  };

  const packagesError = _.get(errors, 'packages')?.message as string;
  return (
    <>
      <div className="my-4 space-y-4">
        <h2 className="block font-bold text-xl">Packages</h2>
        <div className="bg-white rounded grid grid-cols-3 gap-4">
          <button
            onClick={() => handlePackageModelOpen(packages.length)}
            className="bg-[#f6f6f6] p-4 text-center rounded cursor-pointer flex flex-col items-center justify-center"
            type="button"
          >
            <i className="fas fa-plus text-[#5a5a5a]"></i>
            <p className="font-medium text-[#9b9b9b]">Click here to add Package</p>
          </button>
          {packages.map((packageDetail, index) => (
            <div
              key={index}
              className="bg-[#f6f6f6] p-4 rounded relative text-left cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label={`Edit package ${packageDetail.title}`}
              onClick={() => handlePackageModelOpen(index)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handlePackageModelOpen(index);
                }
              }}
            >
              <p className="text-[15px] font-[600]">{packageDetail.title}</p>
              <p className="text-[15px] font-[450] mt-2 whitespace-pre-line">{packageDetail.description}</p>
              <p className="text-[16px]  font-[600] mt-2">₹ {packageDetail.price}</p>
              <p className="text-[14px] font-[600] mt-2 flex justify-between">
                {isResourcesLinkedToVenue ? (
                  <>
                    Reources To Book:{' '}
                    <span>{(packageDetail as TicketBookingPackageWithoutResources).resourcesToBook}</span>
                  </>
                ) : (
                  <>
                    Ticket: <span>{packageDetail.resources?.count}</span>
                  </>
                )}
              </p>
              <button
                className="absolute top-0 right-0 text-black mt-1 mr-1 rounded-full w-4 h-4 flex items-center justify-center"
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  withErrorHandler(async () => {
                    if (packageDetail.id && listingId && venueId) {
                      await dispatch(
                        deletePackageThunk({ listingId, venueId, packageId: packageDetail.id })
                      ).unwrap();

                      setValue(
                        'packages',
                        packages.filter((_, i) => i !== index)
                      );

                      dispatch(setPackages({ venueId, packages: watch('packages') as Package[] }));
                    }
                  }, `Successfully removed ${packageDetail.title}`);
                }}
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          ))}
        </div>
        {packagesError && <p className="text-red-600 text-sm font-semibold mt-1">{packagesError}</p>}
      </div>
      <PackageModal
        isOpen={displayPackageModal}
        resetState={() => {
          console.log(packagesPreviousState);
          setValue('packages', packagesPreviousState as AddVenueFormData['packages']);
        }}
        onClose={() => {
          setDisplayPackageModal(false);
          setPackageModalIndex(-1);
        }}
        index={packageModalIndex}
        listingId={listingId!}
        venueId={venueId}
        {...{ register, errors, trigger, setValue, watch }}
      />
    </>
  );
};
